import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { mfaApi } from '../api';
import { Btn, PageHeader, Alert, Input } from '../components/ui';
import MfaSetup from './MfaSetup';

export default function MyAccount() {
  const { user, refreshUser }   = useAuth();
  const [mfaStatus, setMfa]     = useState(null);
  const [showSetup, setSetup]   = useState(false);
  const [showDisable, setDis]   = useState(false);
  const [disCode, setDisCode]   = useState('');
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    mfaApi.status()
      .then(r => setMfa(r.data?.data))
      .catch(() => {});
  }, []);

  const handleDisableMfa = async () => {
    if (!disCode.trim() || disCode.trim().length !== 6) {
      setError('Enter the 6-digit code from your authenticator app'); return;
    }
    setSaving(true); setError('');
    try {
      await mfaApi.disable(parseInt(disCode.trim()));
      setSuccess('MFA disabled');
      setMfa({ mfaEnabled: false, mfaSetupComplete: false });
      setShowDisable(false); setDisCode('');
      await refreshUser();
    } catch (e) { setError(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  if (showSetup) return (
    <div style={{ padding: '24px', maxWidth: '520px' }}>
      <MfaSetup
        onComplete={async () => {
          setSetup(false); setSuccess('MFA enabled successfully');
          const r = await mfaApi.status();
          setMfa(r.data?.data);
          await refreshUser();
        }}
        onCancel={() => setSetup(false)}
      />
    </div>
  );

  return (
    <div style={{ padding: '24px', maxWidth: '600px' }}>
      <PageHeader title="My Account" subtitle="Manage your security settings" />

      {error   && <Alert type="error"   message={error}   />}
      {success && <Alert type="success" message={success} />}

      {/* Profile info */}
      <div style={S.section}>
        <p style={S.sectionTitle}>Profile</p>
        <div style={S.infoGrid}>
          <InfoRow label="Name"        value={user?.name} />
          <InfoRow label="Employee ID" value={user?.employeeId} />
          <InfoRow label="Role"        value={user?.role?.replace('_', ' ')} />
          <InfoRow label="Company ID"  value={user?.companyId} />
        </div>
      </div>

      {/* MFA Section */}
      <div style={S.section}>
        <p style={S.sectionTitle}>Two-Factor Authentication (MFA)</p>

        <div style={S.mfaCard}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
            <span style={{ fontSize: '28px' }}>{mfaStatus?.mfaEnabled ? '🔐' : '🔓'}</span>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, fontSize: '15px', color: 'var(--color-text)', marginBottom: '4px' }}>
                {mfaStatus?.mfaEnabled ? 'MFA is enabled' : 'MFA is not enabled'}
              </p>
              <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', lineHeight: 1.65 }}>
                {mfaStatus?.mfaEnabled
                  ? 'Your account is protected. Every login requires your authenticator app.'
                  : 'Enable MFA to protect your account with your company phone.'}
              </p>
            </div>
            <div style={{ flexShrink: 0 }}>
              {mfaStatus?.mfaEnabled ? (
                <Btn size="sm" variant="ghost" onClick={() => setShowDisable(d => !d)}>
                  Disable MFA
                </Btn>
              ) : (
                <Btn size="sm" onClick={() => setSetup(true)}>
                  Enable MFA →
                </Btn>
              )}
            </div>
          </div>

          {/* Disable MFA form */}
          {showDisable && mfaStatus?.mfaEnabled && (
            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--color-border)' }}>
              <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
                Enter your current authenticator code to confirm disabling MFA:
              </p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <Input
                    label="Current 6-digit code"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={disCode}
                    onChange={e => setDisCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                  />
                </div>
                <Btn
                  variant="danger"
                  size="sm"
                  onClick={handleDisableMfa}
                  loading={saving}
                  style={{ marginBottom: '2px' }}
                >
                  Confirm Disable
                </Btn>
              </div>
            </div>
          )}
        </div>

        <div style={S.mfaNote}>
          <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', lineHeight: 1.65 }}>
            <strong>Lost your phone?</strong> Contact your Super Admin or Mahashunya support to reset MFA.
            Without your authenticator app, you cannot log in if MFA is enabled.
          </p>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: '16px', padding: '10px 0', borderBottom: '1px solid var(--color-border)' }}>
      <span style={{ width: '120px', fontSize: '13px', color: 'var(--color-text-muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '13px', color: 'var(--color-text)', fontWeight: 500 }}>{value || '—'}</span>
    </div>
  );
}

const S = {
  section: { background: '#fff', border: '1px solid var(--color-border)', borderRadius: '14px', padding: '20px', marginBottom: '16px' },
  sectionTitle: { fontSize: '13px', fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '14px' },
  infoGrid: { display: 'flex', flexDirection: 'column' },
  mfaCard: { background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: '12px', padding: '16px', marginBottom: '10px' },
  mfaNote: { background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '10px', padding: '12px 14px' },
};
